import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Snackbar
} from '@mui/material';
import { Edit as EditIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import ReactMarkdown from 'react-markdown';

function Agents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  
  // Combined Test/Edit Dialog state
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [testMetadata, setTestMetadata] = useState('{}');
  const [testResponse, setTestResponse] = useState(null);
  const [testError, setTestError] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  
  // Agent configuration form
  const [configForm, setConfigForm] = useState({
    systemInstructions: '',
    modelId: '',
    prompt: '',
    temperature: 0,
    topP: 0,
    topK: 0,
    testUrl: ''
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const agentsCollection = collection(db, 'agents');
      const agentsSnapshot = await getDocs(agentsCollection);
      const agentsList = agentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAgents(agentsList);
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Failed to load agents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTestDialog = (agent) => {
    setSelectedAgent(agent);
    setConfigForm({
      systemInstructions: agent.systemInstructions || '',
      modelId: agent.modelId || '',
      prompt: agent.prompt || '',
      temperature: agent.temperature || 0,
      topP: agent.topP || 0,
      topK: agent.topK || 0,
      testUrl: agent.testUrl || ''
    });
    setTestMessage(agent.testMessage || '');
    setTestMetadata('{}');
    setTestResponse(null);
    setTestError(null);
    setSaveError(null);
    setTestDialogOpen(true);
  };

  const handleCloseTestDialog = () => {
    setTestDialogOpen(false);
    setSelectedAgent(null);
    setTestMessage('');
    setTestMetadata('{}');
    setTestResponse(null);
    setTestError(null);
    setSaveError(null);
  };

  const handleConfigChange = (field, value) => {
    setConfigForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setSaveError(null);
      
      const agentRef = doc(db, 'agents', selectedAgent.id);
      
      const updateData = {
        systemInstructions: configForm.systemInstructions,
        modelId: configForm.modelId,
        prompt: configForm.prompt,
        temperature: parseFloat(configForm.temperature),
        topP: parseFloat(configForm.topP),
        topK: parseInt(configForm.topK, 10),
        testUrl: configForm.testUrl
      };
      
      await updateDoc(agentRef, updateData);
      
      // Update local state
      setAgents(prev => prev.map(agent => 
        agent.id === selectedAgent.id 
          ? { ...agent, ...updateData }
          : agent
      ));
      
      // Show success message and close dialog
      setSuccessSnackbar(true);
      handleCloseTestDialog();
    } catch (err) {
      console.error('Error updating agent:', err);
      setSaveError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestAgent = async () => {
    try {
      setTesting(true);
      setTestError(null);
      setTestResponse(null);

      // Check if testUrl is configured
      if (!configForm.testUrl) {
        setTestError('Test URL is required. Please provide a test URL.');
        setTesting(false);
        return;
      }

      // Parse metadata
      let metadata;
      try {
        metadata = JSON.parse(testMetadata);
      } catch (err) {
        setTestError('Invalid JSON in metadata field');
        setTesting(false);
        return;
      }

      // Build request body with all configuration parameters
      const requestBody = {
        message: testMessage,
        metadata: metadata,
        systemInstructions: configForm.systemInstructions,
        modelId: configForm.modelId,
        prompt: configForm.prompt,
        temperature: parseFloat(configForm.temperature),
        topP: parseFloat(configForm.topP),
        topK: parseInt(configForm.topK, 10)
      };

      // Make API call using the configured testUrl
      const response = await fetch(configForm.testUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      if (!response.ok) {
        setTestError(`API Error: ${data.error || response.statusText}`);
      } else {
        setTestResponse(data);
      }
    } catch (err) {
      console.error('Error testing agent:', err);
      setTestError(`Request failed: ${err.message}`);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Agent Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure AI agent parameters including system instructions, model settings, and generation parameters.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Agent ID</strong></TableCell>
              <TableCell><strong>Model ID</strong></TableCell>
              <TableCell><strong>Temperature</strong></TableCell>
              <TableCell><strong>Top P</strong></TableCell>
              <TableCell><strong>Top K</strong></TableCell>
              <TableCell><strong>Test URL</strong></TableCell>
              <TableCell><strong>System Instructions</strong></TableCell>
              <TableCell><strong>Prompt</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No agents found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              agents.map((agent) => (
                <TableRow key={agent.id} hover>
                  <TableCell>
                    <Chip label={agent.id} size="small" />
                  </TableCell>
                  <TableCell>{agent.modelId || '-'}</TableCell>
                  <TableCell>{agent.temperature ?? '-'}</TableCell>
                  <TableCell>{agent.topP ?? '-'}</TableCell>
                  <TableCell>{agent.topK ?? '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap>
                      {agent.testUrl || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap>
                      {agent.systemInstructions || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography variant="body2" noWrap>
                      {agent.prompt || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleOpenTestDialog(agent)}
                      title="Test & Configure Agent"
                    >
                      <PlayArrowIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Test & Configure Agent Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={handleCloseTestDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Test & Configure Agent: {selectedAgent?.id}
        </DialogTitle>
        <DialogContent>
          {selectedAgent?.testInstructions && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedAgent.testInstructions}
              </Typography>
            </Alert>
          )}
          
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveError}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure agent parameters and test with the API. If you like the results, save the settings.
          </Typography>

          {/* Configuration Section */}
          <Card sx={{ mb: 3 }}>
            <CardHeader 
              title="Agent Configuration" 
              titleTypographyProps={{ variant: 'h6' }}
            />
            <Divider />
            <CardContent>
              <TextField
                fullWidth
                label="Test URL"
                value={configForm.testUrl}
                onChange={(e) => handleConfigChange('testUrl', e.target.value)}
                margin="normal"
                variant="outlined"
                placeholder="http://127.0.0.1:5001/project-id/region/function-name"
                helperText="API endpoint URL for testing this agent"
              />

              <TextField
                fullWidth
                label="Model ID"
                value={configForm.modelId}
                onChange={(e) => handleConfigChange('modelId', e.target.value)}
                margin="normal"
                variant="outlined"
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Temperature"
                  type="number"
                  value={configForm.temperature}
                  onChange={(e) => handleConfigChange('temperature', e.target.value)}
                  variant="outlined"
                  inputProps={{ step: 0.1, min: 0, max: 2 }}
                  helperText="Controls randomness (0-2)"
                />
                
                <TextField
                  fullWidth
                  label="Top P"
                  type="number"
                  value={configForm.topP}
                  onChange={(e) => handleConfigChange('topP', e.target.value)}
                  variant="outlined"
                  inputProps={{ step: 0.1, min: 0, max: 1 }}
                  helperText="Nucleus sampling (0-1)"
                />
                
                <TextField
                  fullWidth
                  label="Top K"
                  type="number"
                  value={configForm.topK}
                  onChange={(e) => handleConfigChange('topK', e.target.value)}
                  variant="outlined"
                  inputProps={{ step: 1, min: 0 }}
                  helperText="Top tokens to consider"
                />
              </Box>
              
              <TextField
                fullWidth
                label="System Instructions"
                value={configForm.systemInstructions}
                onChange={(e) => handleConfigChange('systemInstructions', e.target.value)}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                helperText="Core instructions for the agent"
              />
              
              <TextField
                fullWidth
                label="Prompt"
                value={configForm.prompt}
                onChange={(e) => handleConfigChange('prompt', e.target.value)}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                helperText="Default prompt template"
              />
            </CardContent>
          </Card>

          {/* Test Section */}
          <Card sx={{ mb: 2 }}>
            <CardHeader 
              title="Test Agent" 
              titleTypographyProps={{ variant: 'h6' }}
            />
            <Divider />
            <CardContent>
              <TextField
                fullWidth
                label="Message"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                margin="normal"
                variant="outlined"
                multiline
                rows={3}
                placeholder="Enter your test message here..."
              />
              
              <TextField
                fullWidth
                label="Metadata (JSON)"
                value={testMetadata}
                onChange={(e) => setTestMetadata(e.target.value)}
                margin="normal"
                variant="outlined"
                multiline
                rows={2}
                placeholder='{"key": "value"}'
                helperText="Enter valid JSON object"
              />
            </CardContent>
          </Card>

          {testError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {testError}
            </Alert>
          )}

          {testResponse && (
            <Card sx={{ mb: 2 }}>
              <CardHeader 
                title="API Response" 
                titleTypographyProps={{ variant: 'h6' }}
                sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}
              />
              <Divider />
              <CardContent>
                <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 400, overflow: 'auto' }}>
                  <ReactMarkdown>
                    {testResponse}
                  </ReactMarkdown>
                </Paper>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestDialog} disabled={testing || saving}>
            Close
          </Button>
          <Button
            onClick={handleTestAgent}
            variant="contained"
            color="primary"
            disabled={testing || !testMessage || !configForm.testUrl}
            startIcon={testing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
          >
            {testing ? 'Testing...' : 'Test API'}
          </Button>
          <Button
            onClick={handleSaveSettings}
            variant="contained"
            color="success"
            disabled={!testResponse || saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={successSnackbar}
        autoHideDuration={3000}
        onClose={() => setSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          Settings saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Agents;
